import { lazy, type ComponentType } from 'react';

/**
 * Route-level code splitting that the prerenderer can wait for.
 *
 * The prerenderer snapshots the DOM when the app dispatches `render-event`.
 * With plain React.lazy that event could fire while a route chunk was still in
 * flight, and the page was written to disk holding nothing but the layout — three
 * pages shipped with 10 words each before this existed. Tracking the in-flight
 * factories lets main.tsx hold the snapshot until the real content is mounted.
 */
let pending = 0;
const waiters: Array<() => void> = [];

function settle() {
  pending -= 1;
  if (pending === 0) waiters.splice(0).forEach((resolve) => resolve());
}

export function routeLazy<P extends object>(factory: () => Promise<{ default: ComponentType<P> }>) {
  return lazy(() => {
    pending += 1;
    return factory().then(
      (mod) => {
        settle();
        return mod;
      },
      (err) => {
        settle();
        throw err;
      },
    );
  });
}

/** Resolves once no route chunk is loading. */
export function routeChunksSettled(): Promise<void> {
  if (pending === 0) return Promise.resolve();
  return new Promise((resolve) => waiters.push(resolve));
}
